<template>
  <section id="services" class="services-section">
    <v-container>
      <div class="section-heading">
        <div class="eyebrow">Services & price guide</div>
        <h2>Find laboratory, imaging, drug testing, and consultation services faster.</h2>
        <p>
          Browse the provided MayonLab laboratory, imaging, vaccine, package, and support-service price list. Search by test name or open a service group to narrow the results.
        </p>
        <p v-if="standalone" class="section-heading__counts">The searchable guide covers {{ services.length }} services across {{ serviceCategories.length }} service groups.</p>
      </div>

      <v-row class="category-grid">
        <v-col v-for="category in serviceCategories" :key="category.name" cols="12" sm="6" lg="4">
          <v-card class="category-card" elevation="0" @click="selectCategory(category.name)">
            <span class="category-card__icon"><i :class="category.icon" aria-hidden="true"></i></span>
            <div>
              <h3>{{ category.name }}</h3>
              <p>{{ category.text }}</p>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="service-explorer" elevation="0">
        <div class="service-explorer__topline">
          <div>
            <span class="service-explorer__eyebrow">2026 service price guide</span>
            <h3>{{ explorerTitle }}</h3>
          </div>
          <v-btn v-if="hasActiveFilters" class="mayon-btn mayon-btn--ghost" text @click="resetFilters">
            Reset filters
          </v-btn>
        </div>

        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              outlined
              rounded
              dense
              hide-details
              label="Search services, tests, or packages"
            />
          </v-col>
          <v-col cols="12" md="7">
            <div class="chip-row">
              <v-chip
                v-for="category in categories"
                :key="category"
                :color="selectedCategory === category ? '#4870c8' : '#f1f4f8'"
                :text-color="selectedCategory === category ? 'white' : '#344054'"
                class="mr-2 mb-2"
                @click="setCategory(category)"
              >
                {{ category }}
              </v-chip>
            </div>
          </v-col>
        </v-row>

        <div class="service-explorer__meta">
          <span>
            Showing {{ displayedServices.length }} of {{ filteredServices.length }} matched services
          </span>
          <span v-if="hiddenCount > 0" class="service-explorer__hint">
            {{ hiddenCount }} more hidden to keep this section compact.
          </span>
        </div>

        <div class="desktop-table">
          <v-simple-table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Category</th>
                <th class="text-right">Price</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in displayedServices" :key="item.name + item.category">
                <td class="service-name">{{ item.name }}</td>
                <td>{{ item.category }}</td>
                <td class="service-price text-right">{{ item.price }}</td>
                <td>{{ item.notes || '—' }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>

        <div class="mobile-service-list">
          <v-card v-for="item in displayedServices" :key="item.name + item.category" class="mobile-service-card" elevation="0">
            <div>
              <h3>{{ item.name }}</h3>
              <p>{{ item.category }}</p>
            </div>
            <strong>{{ item.price }}</strong>
            <small v-if="item.notes">{{ item.notes }}</small>
          </v-card>
        </div>

        <div class="service-explorer__actions">
          <v-btn
            v-if="filteredServices.length > previewLimit"
            class="mayon-btn mayon-btn--primary"
            @click="toggleExpanded"
          >
            {{ isExpanded ? 'Show fewer services' : `View ${hiddenCount} more services` }}
          </v-btn>
          <v-btn class="mayon-btn mayon-btn--secondary" to="/branches">
            Confirm with branch
          </v-btn>
        </div>

        <v-alert text color="#4870c8" class="price-disclaimer">
          Rates are transcribed from the provided 2026 MayonLab price files. Prices, preparation requirements, schedules, branch availability, vaccine stock, HMO coverage, and final charges should still be confirmed before visiting.
        </v-alert>

        <div class="section-links">
          <router-link to="/services" v-if="!standalone">View all services</router-link>
          <router-link to="/packages">Compare packages</router-link>
          <router-link to="/branches">Confirm with branches</router-link>
        </div>
      </v-card>
    </v-container>
  </section>
</template>

<script>
import { serviceCategories, services } from '@/data/mayonSite'

const normalize = (value) => String(value || '').trim().toLowerCase()

export default {
  name: 'OtherService',
  props: {
    standalone: { type: Boolean, default: false }
  },
  data() {
    return {
      search: '',
      selectedCategory: 'All',
      isExpanded: false,
      previewLimit: 12,
      serviceCategories,
      services
    }
  },
  computed: {
    categories() {
      return ['All'].concat(this.serviceCategories.map((category) => category.name))
    },
    activeCategoryConfig() {
      if (this.selectedCategory === 'All') return null
      return this.serviceCategories.find((category) => category.name === this.selectedCategory) || null
    },
    normalizedSearch() {
      return this.search.trim().toLowerCase()
    },
    hasActiveFilters() {
      return this.selectedCategory !== 'All' || Boolean(this.normalizedSearch)
    },
    filteredServices() {
      return this.services.filter((item) => {
        const matchesCategory = this.matchesSelectedCategory(item)
        const matchesSearch = !this.normalizedSearch || [item.name, item.category, item.notes]
          .join(' ')
          .toLowerCase()
          .includes(this.normalizedSearch)
        return matchesCategory && matchesSearch
      })
    },
    displayedServices() {
      if (this.isExpanded) return this.filteredServices
      return this.filteredServices.slice(0, this.previewLimit)
    },
    hiddenCount() {
      return Math.max(this.filteredServices.length - this.displayedServices.length, 0)
    },
    explorerTitle() {
      if (this.hasActiveFilters) return 'Filtered service results'
      return 'Popular services preview'
    }
  },
  watch: {
    search() {
      this.isExpanded = false
    },
    selectedCategory() {
      this.isExpanded = false
    }
  },
  methods: {
    setCategory(category) {
      this.selectedCategory = category
    },
    matchesSelectedCategory(item) {
      if (this.selectedCategory === 'All') return true
      const config = this.activeCategoryConfig
      if (!config) return item.category === this.selectedCategory

      const categories = config.filterCategories || []
      if (categories.includes(item.category)) return true

      const terms = config.filterTerms || []
      if (!terms.length) return false

      const haystack = normalize([item.name, item.category, item.notes].join(' '))
      return terms.some((term) => haystack.includes(normalize(term)))
    },
    selectCategory(category) {
      this.selectedCategory = category
      this.$nextTick(() => {
        const target = document.querySelector('.service-explorer')
        if (!target) return
        const y = target.getBoundingClientRect().top + window.pageYOffset - 88
        window.scrollTo({ behavior: 'smooth', top: y })
      })
    },
    resetFilters() {
      this.search = ''
      this.selectedCategory = 'All'
      this.isExpanded = false
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded
      if (!this.isExpanded) {
        this.$nextTick(() => {
          const target = document.querySelector('.service-explorer')
          if (!target) return
          const y = target.getBoundingClientRect().top + window.pageYOffset - 88
          window.scrollTo({ behavior: 'smooth', top: y })
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.services-section {
  background: #ffffff;
  padding: 5rem 0;
}

.section-heading {
  margin: 0 auto 2.25rem;
  max-width: 780px;
  text-align: center;

  .eyebrow {
    color: var(--mayon-blue);
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    margin-bottom: 0.7rem;
    text-transform: uppercase;
  }

  h2 {
    color: var(--mayon-navy);
    font-size: clamp(2rem, 4vw, 3rem);
    letter-spacing: -0.035em;
    line-height: 1.1;
    margin-bottom: 1rem;
  }

  p {
    color: var(--mayon-muted);
    line-height: 1.8;
  }

  &__counts {
    color: var(--mayon-blue);
    font-weight: 800;
  }
}

.section-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.25rem;

  a {
    color: var(--mayon-blue);
    font-weight: 800;
    text-decoration: none;
  }
}

.category-grid {
  margin-bottom: 1rem;
}

.category-card {
  align-items: flex-start;
  background: #ffffff !important;
  border: 1px solid var(--mayon-border) !important;
  border-radius: 22px !important;
  box-shadow: none !important;
  cursor: pointer;
  display: flex;
  gap: 1rem;
  height: 100%;
  padding: 1.25rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    background: #fbfdff !important;
    border-color: rgba(88, 184, 184, 0.36) !important;
    box-shadow: var(--mayon-shadow-card) !important;
    transform: translateY(-3px);
  }

  &__icon {
    align-items: center;
    background: rgba(88, 184, 184, 0.10);
    border-radius: 18px;
    color: var(--mayon-blue);
    display: inline-flex;
    flex: 0 0 56px;
    height: 56px;
    justify-content: center;
    width: 56px;

    i {
      font-size: 1.32rem;
    }
  }

  h3 {
    color: var(--mayon-navy);
    font-size: 1rem;
    margin-bottom: 0.4rem;
  }

  p {
    color: var(--mayon-muted);
    display: -webkit-box;
    font-size: 0.92rem;
    line-height: 1.55;
    margin: 0;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
}

.service-explorer {
  border: 1px solid var(--mayon-border) !important;
  border-radius: 28px !important;
  box-shadow: var(--mayon-shadow-card) !important;
  margin-top: 1.5rem;
  overflow: hidden;
  padding: 1.4rem;

  &__topline {
    align-items: flex-start;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    margin-bottom: 1.25rem;

    h3 {
      color: var(--mayon-navy);
      font-size: 1.4rem;
      letter-spacing: -0.025em;
      margin: 0.25rem 0 0;
    }
  }

  &__eyebrow {
    color: var(--mayon-blue);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  &__meta {
    align-items: center;
    color: var(--mayon-muted);
    display: flex;
    flex-wrap: wrap;
    font-size: 0.92rem;
    font-weight: 700;
    gap: 0.7rem;
    justify-content: space-between;
    margin: 1rem 0;
  }

  &__hint {
    background: rgba(88, 184, 184, 0.10);
    border: 1px solid rgba(88, 184, 184, 0.24);
    border-radius: 999px;
    color: var(--mayon-blue);
    padding: 0.3rem 0.7rem;
  }

  &__actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.7rem;
    justify-content: center;
    margin-top: 1.25rem;
  }
}

.service-explorer::v-deep .v-input__slot {
  border-color: var(--mayon-border) !important;
  box-shadow: none !important;
}

.service-explorer::v-deep .v-label {
  color: var(--mayon-muted) !important;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.chip-row::v-deep .v-chip {
  border: 1px solid rgba(227, 234, 242, 0.9) !important;
  font-weight: 700;
}

.desktop-table {
  border: 1px solid var(--mayon-border);
  border-radius: 18px;
  overflow: hidden;

  th {
    background: var(--mayon-soft);
    color: #344054 !important;
    font-weight: 800 !important;
    text-transform: uppercase;
  }

  td,
  th {
    border-bottom: 1px solid rgba(227, 234, 242, 0.72) !important;
    font-size: 0.92rem !important;
  }

  tbody tr:hover {
    background: #fbfdff;
  }
}

.service-name,
.service-price {
  color: var(--mayon-navy);
  font-weight: 900;
}

.mobile-service-list {
  display: none;
}

.price-disclaimer {
  background: rgba(72, 112, 200, 0.08) !important;
  border: 1px solid rgba(72, 112, 200, 0.12) !important;
  border-radius: 18px !important;
  margin: 1.25rem 0 0 !important;
}

@media (max-width: 760px) {
  .chip-row {
    justify-content: flex-start;
  }

  .service-explorer__topline {
    display: block;
  }

  .desktop-table {
    display: none;
  }

  .mobile-service-list {
    display: grid;
    gap: 0.8rem;
  }

  .mobile-service-card {
    border: 1px solid var(--mayon-border) !important;
    border-radius: 18px !important;
    padding: 1rem;

    h3 {
      color: var(--mayon-navy);
      font-size: 1rem;
      margin-bottom: 0.2rem;
    }

    p {
      color: var(--mayon-muted);
      margin-bottom: 0.6rem;
    }

    strong {
      color: var(--mayon-blue);
      display: block;
      font-size: 1.25rem;
      margin-bottom: 0.25rem;
    }

    small {
      color: var(--mayon-muted);
      display: block;
      line-height: 1.5;
    }
  }

  .service-explorer__actions .v-btn {
    width: 100%;
  }
}
</style>
